import type { SupabaseClient } from "@supabase/supabase-js";

import { supabase } from "./supabase";

/**
 * Station provider types
 * Must match the 'provider' field in Station interface
 */
export type StationProvider =
  | "radio-browser"
  | "sr"
  | "sveriges-radio"
  | "podcast"
  | "nrk"
  | "rtp"
  | "abc"
  | "rnz"
  | "yle"
  | "custom"
  | "bauer"
  | "viaplay"
  | "dbmedia"
  | "kexp"
  | "bbc"
  | "nent"
  | "independent"
  | "icecast";

/**
 * Station logo record from database
 */
export interface StationLogoRecord {
  id: string;
  station_id: string;
  provider: StationProvider;
  logo_path: string;
  quality: string;
  source: string;
  created_at: string;
  updated_at: string;
}

/**
 * Get HD logo URL for a station from Supabase
 *
 * Resolution order:
 * 1. Check station_logos table for HD logo
 * 2. If found, get public URL from Supabase Storage
 * 3. Return null if not found (caller should fallback to origin URL)
 *
 * @param params - Station identifier and provider
 * @returns Full public URL to HD logo, or null if not available
 *
 * @example
 * ```ts
 * const hdUrl = await getHdLogoForStation({
 *   stationId: 'dac458a3-62f4-11e8-9b3d-52543be04c81',
 *   provider: 'radio-browser'
 * });
 *
 * if (hdUrl) {
 *   // Use HD logo
 * } else {
 *   // Fallback to origin favicon/logoUrl
 * }
 * ```
 */
export async function getHdLogoForStation(params: {
  stationId: string;
  provider: StationProvider;
  client?: SupabaseClient; // Optional client for server-side usage
}): Promise<string | null> {
  try {
    const client = params.client || supabase;

    // Query station_logos table for this station
    // Prioritize 'hd' quality, but accept any if multiple exist
    const { data, error } = await client
      .from("station_logos")
      .select("*")
      .eq("station_id", params.stationId)
      .eq("provider", params.provider)
      .order("quality", { ascending: false }) // 'hd' comes before 'default' alphabetically
      .limit(1)
      .single();

    if (error) {
      // No logo found, or database error
      if (error.code !== "PGRST116") {
        // PGRST116 = no rows
        console.warn("[getHdLogoForStation] Database error:", error);
      }
      return null;
    }

    if (!data) {
      return null;
    }

    // Get public URL from Supabase Storage
    // Bucket name: 'station-logos' (must be created manually in Supabase UI)
    const { data: urlData } = client.storage
      .from("station-logos")
      .getPublicUrl(data.logo_path);

    if (!urlData?.publicUrl) {
      console.warn(
        "[getHdLogoForStation] Failed to get public URL for:",
        data.logo_path,
      );
      return null;
    }

    return urlData.publicUrl;
  } catch (error) {
    console.error("[getHdLogoForStation] Unexpected error:", error);
    return null;
  }
}

/**
 * Helper to normalize provider names between Station.provider and our DB
 * Some stations use 'sr', others 'sveriges-radio'
 */
export function normalizeProvider(provider: string): StationProvider {
  // Map 'sr' to 'sveriges-radio' for consistency
  if (provider === "sr") {
    return "sveriges-radio";
  }
  return provider as StationProvider;
}

/**
 * Get station identifier for HD logo lookup
 * Different providers store IDs in different fields
 *
 * @param station - Station object
 * @returns Station ID suitable for HD logo lookup, or null
 */
export function getStationIdForLookup(station: {
  id: string;
  provider: string;
}): string | null {
  // RadioBrowser uses UUIDs as station IDs
  if (station.provider === "radio-browser") {
    return station.id;
  }

  // Sveriges Radio - extract channel ID from station.id
  // Format is typically 'sr-{channelId}' or just the channel ID
  if (station.provider === "sr" || station.provider === "sveriges-radio") {
    const match = station.id.match(/^(?:sr-)?(\d+)$/);
    return match ? match[1] : station.id;
  }

  // For other providers, use the station ID as-is
  return station.id;
}
